/**
 * `pack_queued`/`pack_unqueued` for BOTH the anon and the authenticated queue path (GA4
 * collection policy, `@core/utils/CLAUDE.md`: a browser is present at both call sites, so both
 * fire client-side). The anon path calls this directly from `usePackQueue.ts`'s localStorage
 * branch (no `PackQueueItem` row is possible for an anonymous visitor — the schema's `userId` is
 * non-nullable, see `packQueue.server.ts`'s header comment); the authenticated path calls it from
 * the same hook once the `/api/pack-queue` fetcher's response resolves, converging what used to
 * be two separate collection mechanisms onto one.
 *
 * Calls `track()` (not `narcisTrack`/`ovidTrack`) directly against the shared `FunnelEventMap` —
 * this module lives in `@core/`, which must not import a `@product/`-scoped tracker (see
 * `NarcisEventMap`'s own header comment on the @core/@product boundary). `track()`'s bridge to
 * `CoreEventMap` is the same `as any` seam every product tracker already crosses.
 */
import {track, type FunnelEventMap} from '@core/utils/analytics'
import type {PackQueueContext} from '@prisma/client'

// Type-only import, erased at compile time — no client-bundle concern (this module is imported
// from the browser; see the header comment above). Single source of truth for the three queue
// contexts is the Prisma schema's `PackQueueContext` enum (schema.prisma) — a hand-declared union
// here would drift from it silently.
export type FunnelQueueEventContext = PackQueueContext

export type FunnelQueueEvent =
    | {name: 'pack_queued'; packSlug: string; context: FunnelQueueEventContext}
    | {name: 'pack_unqueued'; packSlug: string; context: FunnelQueueEventContext}

export function emitFunnelQueueEvent(event: FunnelQueueEvent): void {
    const params: FunnelEventMap['pack_queued'] | FunnelEventMap['pack_unqueued'] = {
        pack_slug: event.packSlug,
        context: event.context,
    }
    track(event.name as any, params as any)
}
